import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  Settings,
  Zap,
  FileText,
  AlertCircle,
  Plus,
  Trash2,
  RefreshCw,
  Eye,
  EyeOff,
  TestTube,
  Save,
  Clock,
  CheckCircle,
  XCircle,
  Activity,
  Globe
} from 'lucide-react';
import { toast } from 'react-toastify';
import watiService from '../../../services/integrations/watiService';
import SettingsSlideModal from '../../../components/Modal/SettingsSlideModal';

interface Template {
  id: string;
  name: string;
  status: 'APPROVED' | 'PENDING' | 'REJECTED';
  category: string;
  language: string;
  parameters: string[];
  content: string;
  headerType?: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  parameter_mapping?: Record<string, string> | null;
}

interface TriggerRule {
  id: string;
  name?: string;
  trigger_name?: string;
  triggerType?: 'new_lead' | 'status_change' | 'custom';
  trigger_event?: 'new_lead' | 'status_change' | 'custom';
  fromStatus?: string;
  toStatus?: string;
  templateId?: string;
  template_id?: string;
  parameterMapping?: Record<string, string>;
  parameter_mapping?: Record<string, string>;
  trigger_conditions?: {
    from_status?: string;
    to_status?: string;
    target_status?: string;
  };
  isActive?: boolean;
  is_active?: boolean;
  channel?: string;
  priority?: number;
}

const WatiSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'config' | 'templates' | 'automation' | 'logs'>('config');
  const [isSaving, setIsSaving] = useState(false);
  const [showApiToken, setShowApiToken] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [triggers, setTriggers] = useState<TriggerRule[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showTriggerSlider, setShowTriggerSlider] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({
    type: null,
    message: ''
  });

  // Configuration State
  const [config, setConfig] = useState({
    apiEndpoint: '',
    accessToken: '',
    tenantId: '',
    isActive: false,
    webhookUrl: ''
  });

  // Message Logs State
  const [logs, setLogs] = useState<any[]>([]);
  const [logsFilter, setLogsFilter] = useState<'all' | 'sent' | 'failed'>('all');

  // Automation Rule Creation State
  const [crmColumns, setCrmColumns] = useState<any[]>([]);
  const [statusOptions, setStatusOptions] = useState<any[]>([]);
  const [editingTrigger, setEditingTrigger] = useState<TriggerRule | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{show: boolean; trigger: any | null}>({show: false, trigger: null});

  // Template-level parameter mapping state
  const [expandedMappingTemplate, setExpandedMappingTemplate] = useState<string | null>(null);
  const [templateMappings, setTemplateMappings] = useState<Record<string, Record<string, string>>>({});
  const [savingMapping, setSavingMapping] = useState<string | null>(null);

  // Load configuration on mount
  useEffect(() => {
    loadConfiguration();
    if (activeTab === 'templates') {
      loadTemplates();
      loadCrmData();
    }
    if (activeTab === 'automation') {
      loadTemplates(); // Load templates for automation modal
      loadTriggers();
      loadCrmData();
    }
    if (activeTab === 'logs') {
      loadLogs();
    }
  }, [activeTab]);

  const loadConfiguration = async () => {
    try {
      const settings = await watiService.getSettings();
      if (settings) {
        setConfig({
          apiEndpoint: settings.apiEndpoint,
          accessToken: settings.accessToken,
          tenantId: settings.tenantId,
          isActive: settings.isActive,
          webhookUrl: ''
        });
      }
    } catch (error) {
      toast.error('Failed to load configuration');
      console.error('Error loading WATI config:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      const templatesData = await watiService.getTemplates();
      const formattedTemplates = templatesData.map((template: any) => ({
        id: template.id,
        name: template.element_name,
        status: template.status,
        category: template.category,
        language: template.language,
        parameters: template.parameters || [],
        content: template.body,
        headerType: template.metadata?.type || 'TEXT',
        parameter_mapping: template.parameter_mapping || null
      }));
      setTemplates(formattedTemplates);
      // Seed editing state from saved mappings
      const initial: Record<string, Record<string, string>> = {};
      formattedTemplates.forEach(t => {
        initial[t.id] = t.parameter_mapping ? { ...t.parameter_mapping } : {};
      });
      setTemplateMappings(initial);
    } catch (error) {
      toast.error('Failed to load templates');
      console.error('Error loading templates:', error);
    }
  };

  const loadTriggers = async () => {
    try {
      const triggersData = await watiService.getTriggers();
      const formattedTriggers = triggersData.map((trigger: any) => ({
        id: trigger.id.toString(),
        name: trigger.trigger_name,
        triggerType: trigger.trigger_event,
        fromStatus: trigger.trigger_conditions?.from_status,
        toStatus: trigger.trigger_conditions?.to_status,
        templateId: trigger.template_id.toString(),
        parameterMapping: trigger.parameter_mapping || {},
        isActive: trigger.is_active
      }));
      setTriggers(formattedTriggers);
    } catch (error) {
      toast.error('Failed to load automation rules');
      console.error('Error loading triggers:', error);
    }
  };

  const loadLogs = async () => {
    try {
      const filter = logsFilter === 'all' ? undefined : logsFilter;
      const logsData = await watiService.getMessageLogs(filter);
      const formattedLogs = logsData.messages.map((log: any) => ({
        time: new Date(log.created_at).toLocaleString(),
        recipient: log.recipient,
        template: log.template?.element_name || 'N/A',
        status: log.status,
        details: log.logs?.[0]?.error_message || 'Success'
      }));
      setLogs(formattedLogs);
    } catch (error) {
      toast.error('Failed to load message logs');
      console.error('Error loading logs:', error);
    }
  };

  const loadCrmData = async () => {
    try {
      const [columnsData, statusData] = await Promise.all([
        watiService.getCrmLeadColumns(),
        watiService.getStatusOptions()
      ]);
      setCrmColumns(columnsData);
      setStatusOptions(statusData);
    } catch (error) {
      console.error('Error loading CRM data:', error);
    }
  };

  const handleSaveTemplateMapping = async (templateId: string) => {
    setSavingMapping(templateId);
    try {
      const mapping = templateMappings[templateId] || {};
      const result = await watiService.saveTemplateParameterMapping(templateId, mapping);
      if (result.success) {
        toast.success('Parameter mapping saved');
        setExpandedMappingTemplate(null);
      } else {
        toast.error(result.message || 'Failed to save mapping');
      }
    } catch (error) {
      toast.error('Failed to save mapping');
    } finally {
      setSavingMapping(null);
    }
  };

  const handleSaveConfig = async () => {
    setIsSaving(true);
    try {
      await watiService.saveSettings(config);
      toast.success('Configuration saved successfully');
    } catch (error) {
      toast.error('Failed to save configuration');
      console.error('Error saving config:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setIsTestingConnection(true);
    setConnectionStatus({ type: null, message: '' });

    try {
      const result = await watiService.testConnection();

      if (result.success) {
        setConnectionStatus({
          type: 'success',
          message: result.message || 'Connection successful! Found ' + (result.data?.templateCount || 0) + ' templates.'
        });
        toast.success(result.message || 'Connection successful!');
      } else {
        setConnectionStatus({
          type: 'error',
          message: result.message || 'Connection failed. Please check your credentials.'
        });
        toast.error(result.message || 'Connection failed.');
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      setConnectionStatus({
        type: 'error',
        message: 'Connection failed. Please check your credentials.'
      });
      toast.error('Connection failed. Please check your credentials.');
    } finally {
      setIsTestingConnection(false);
      // Clear status after 5 seconds
      setTimeout(() => setConnectionStatus({ type: null, message: '' }), 5000);
    }
  };

  const handleSyncTemplates = async () => {
    setIsSyncing(true);
    try {
      const result = await watiService.syncTemplates();
      if (result.success) {
        toast.success(result.message || 'Templates synchronized successfully');
        await loadTemplates();
      } else {
        toast.error(result.message || 'Failed to sync templates');
      }
    } catch (error) {
      toast.error('Failed to sync templates');
      console.error('Error syncing templates:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  // Planned for Phase 2: Send test message functionality
  // Currently commented out in templates tab - will be used when Phase 2 is implemented
  // @ts-expect-error - Function reserved for Phase 2 test message functionality
  const handleTestMessage = async (templateId: string) => {
    try {
      // You might want to show a modal to get recipient number and parameters
      const recipient = prompt('Enter recipient phone number (with country code):');
      if (!recipient) return;

      const result = await watiService.sendTestMessage(templateId, recipient, {});
      if (result.success) {
        toast.success(result.message || 'Test message sent successfully');
      } else {
        toast.error(result.message || 'Failed to send test message');
      }
    } catch (error) {
      toast.error('Failed to send test message');
      console.error('Error sending test message:', error);
    }
  };

  // Handler to edit a trigger
  const handleEditTrigger = (trigger: any) => {
    // Set current trigger for update mode
    setEditingTrigger(trigger);

    // Open the modal
    setShowTriggerSlider(true);
  };

  // Handler to show delete confirmation
  const handleDeleteTrigger = (trigger: any) => {
    setDeleteConfirmation({show: true, trigger});
  };

  // Handler to confirm delete
  const confirmDeleteTrigger = async () => {
    if (!deleteConfirmation.trigger) return;

    try {
      const result = await watiService.deleteTrigger(deleteConfirmation.trigger.id);
      if (result.success) {
        toast.success(result.message || 'Automation rule deleted successfully');
        // Remove the trigger from the list
        setTriggers(triggers.filter(t => t.id !== deleteConfirmation.trigger.id));
      } else {
        toast.error(result.message || 'Failed to delete automation rule');
      }
    } catch (error) {
      toast.error('Failed to delete automation rule');
      console.error('Error deleting trigger:', error);
    } finally {
      setDeleteConfirmation({show: false, trigger: null});
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-brandColor p-2 rounded-lg">
                <MessageSquare className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">WATI Integration</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Configure WhatsApp Business API with WATI
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                config.isActive
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
              }`}>
                {config.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6">
          <div className="flex space-x-8 border-b border-gray-200 dark:border-gray-700">
            {[
              { id: 'config', label: 'Configuration', icon: Settings },
              { id: 'templates', label: 'Templates', icon: FileText },
              { id: 'automation', label: 'Automation', icon: Zap },
              { id: 'logs', label: 'Message Logs', icon: Activity }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center space-x-2 px-1 py-3 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-brandColor text-brandColor dark:border-brandColor dark:text-brandColor'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Configuration Tab */}
        {activeTab === 'config' && (
          <div className="max-w-4xl">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    API Configuration
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        API Endpoint
                      </label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          value={config.apiEndpoint}
                          onChange={(e) => setConfig({ ...config, apiEndpoint: e.target.value })}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          placeholder="https://api.wati.io"
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Your WATI API endpoint URL
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Access Token
                      </label>
                      <div className="relative">
                        <input
                          type={showApiToken ? 'text' : 'password'}
                          value={config.accessToken}
                          onChange={(e) => setConfig({ ...config, accessToken: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                          placeholder="Enter your WATI access token"
                        />
                        <button
                          onClick={() => setShowApiToken(!showApiToken)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          {showApiToken ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        You can find your access token in WATI dashboard under API Access
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tenant ID
                      </label>
                      <input
                        type="text"
                        value={config.tenantId}
                        onChange={(e) => setConfig({ ...config, tenantId: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                        placeholder="e.g., 12365"
                      />
                    </div>

                    <div className="flex items-center justify-between py-4 px-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="active"
                          checked={config.isActive}
                          onChange={(e) => setConfig({ ...config, isActive: e.target.checked })}
                          className="h-4 w-4 text-brandColor rounded focus:ring-brandColor"
                        />
                        <label htmlFor="active" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Enable Integration
                        </label>
                      </div>
                      <button
                        onClick={handleTestConnection}
                        disabled={isTestingConnection}
                        className="flex items-center space-x-2 px-4 py-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isTestingConnection ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <TestTube className="h-4 w-4" />
                        )}
                        <span>{isTestingConnection ? 'Testing...' : 'Test Connection'}</span>
                      </button>
                    </div>

                    {/* Connection Status Message */}
                    {connectionStatus.type && (
                      <div className={`mt-4 p-3 rounded-lg flex items-center space-x-2 ${
                        connectionStatus.type === 'success'
                          ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400 border border-green-200 dark:border-green-800'
                          : 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400 border border-red-200 dark:border-red-800'
                      }`}>
                        {connectionStatus.type === 'success' ? (
                          <CheckCircle className="h-5 w-5 flex-shrink-0" />
                        ) : (
                          <XCircle className="h-5 w-5 flex-shrink-0" />
                        )}
                        <span className="text-sm font-medium">{connectionStatus.message}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleSaveConfig}
                    disabled={isSaving}
                    className="flex items-center space-x-2 px-6 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSaving ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    <span>{isSaving ? 'Saving...' : 'Save Configuration'}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Additional WATI Features */}
            <div className="mt-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-700 dark:text-yellow-300">
                  <p className="font-medium mb-1">WATI Features</p>
                  <ul className="list-disc list-inside space-y-1 text-yellow-600 dark:text-yellow-400">
                    <li>Support for broadcast messages</li>
                    <li>Contact management and tagging</li>
                    <li>Session message support (24-hour window)</li>
                    <li>Rich media templates (images, videos, documents)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div>
            <div className="mb-6 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                WhatsApp Templates
              </h3>
              <button
                onClick={handleSyncTemplates}
                disabled={isSyncing}
                className="flex items-center space-x-2 px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                <span>{isSyncing ? 'Syncing...' : 'Sync Templates'}</span>
              </button>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                {templates.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">No templates found</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                      Click "Sync Templates" to fetch templates from WATI
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {templates.map((template) => (
                      <div
                        key={template.id}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center flex-wrap gap-2">
                              <h4 className="font-medium text-gray-900 dark:text-white">
                                {template.name}
                              </h4>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                template.status === 'APPROVED'
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : template.status === 'PENDING'
                                  ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                              }`}>
                                {template.status}
                              </span>
                              {template.headerType && (
                                <span className="px-2 py-1 bg-brandColor/10 text-brandColor dark:bg-brandColor/20 dark:text-brandColor rounded-full text-xs font-medium">
                                  {template.headerType}
                                </span>
                              )}
                              {template.parameters.length > 0 && (() => {
                                const mappedCount = Object.values(templateMappings[template.id] || {}).filter(v => v).length;
                                const totalCount = template.parameters.length;
                                if (mappedCount === 0)
                                  return <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">⚠ Not mapped</span>;
                                if (mappedCount < totalCount)
                                  return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">{mappedCount}/{totalCount} mapped</span>;
                                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">✓ All mapped</span>;
                              })()}
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              Category: {template.category} | Language: {template.language}
                            </p>
                            <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded">
                              <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                                {template.content}
                              </p>
                            </div>
                            {template.parameters.length > 0 && (
                              <div className="mt-3">
                                <div className="flex items-center justify-between">
                                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                    Parameters: {template.parameters.join(', ')}
                                  </p>
                                  <button
                                    onClick={() => setExpandedMappingTemplate(
                                      expandedMappingTemplate === template.id ? null : template.id
                                    )}
                                    className="text-xs text-brandColor hover:underline flex items-center space-x-1"
                                  >
                                    <Settings className="h-3 w-3" />
                                    <span>
                                      {expandedMappingTemplate === template.id ? 'Hide' : 'Map'} Fields
                                      {template.parameter_mapping && Object.keys(template.parameter_mapping).length > 0
                                        ? ` (${Object.keys(template.parameter_mapping).length} mapped)` : ''}
                                    </span>
                                  </button>
                                </div>

                                {expandedMappingTemplate === template.id && (
                                  <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg">
                                    <p className="text-xs text-blue-700 dark:text-blue-400 mb-3">
                                      Map each template parameter to a lead field. This mapping is used by drip campaigns automatically.
                                    </p>
                                    <div className="space-y-2">
                                      {template.parameters.map((param) => (
                                        <div key={param} className="flex items-center space-x-2">
                                          <span className="text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded min-w-[80px]">
                                            {`{{${param}}}`}
                                          </span>
                                          <span className="text-xs text-gray-500">→</span>
                                          <select
                                            value={(templateMappings[template.id] || {})[param] || ''}
                                            onChange={(e) => setTemplateMappings(prev => ({
                                              ...prev,
                                              [template.id]: {
                                                ...(prev[template.id] || {}),
                                                [param]: e.target.value
                                              }
                                            }))}
                                            className="flex-1 text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                          >
                                            <option value="">-- Select lead field --</option>
                                            {crmColumns.map((col: any) => (
                                              <option key={col.col_id} value={col.col_name}>{col.col_label} ({col.col_name})</option>
                                            ))}
                                          </select>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="mt-3 flex justify-end space-x-2">
                                      <button
                                        onClick={() => setExpandedMappingTemplate(null)}
                                        className="text-xs px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={() => handleSaveTemplateMapping(template.id)}
                                        disabled={savingMapping === template.id}
                                        className="text-xs px-3 py-1.5 bg-brandColor text-white rounded hover:bg-brandColor/90 disabled:opacity-50 flex items-center space-x-1"
                                      >
                                        <Save className="h-3 w-3" />
                                        <span>{savingMapping === template.id ? 'Saving...' : 'Save Mapping'}</span>
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Automation Tab */}
        {activeTab === 'automation' && (
          <div>
            <div className="mb-6 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Automation Rules
              </h3>
              <button
                onClick={() => {
                  setEditingTrigger(null);
                  setShowTriggerSlider(true);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Add Rule</span>
              </button>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                {triggers.length === 0 ? (
                  <div className="text-center py-12">
                    <Zap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">No automation rules configured</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                      Create rules to automatically send WhatsApp messages
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {triggers.map((trigger) => (
                      <div
                        key={trigger.id}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h4 className="font-medium text-gray-900 dark:text-white">
                                {trigger.name}
                              </h4>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                trigger.isActive
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                              }`}>
                                {trigger.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <div className="space-y-1 mt-1">
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                Trigger: {trigger.triggerType === 'new_lead' ? 'New Lead' :
                                         trigger.triggerType === 'status_change' ? 'Status Change' : 'Custom'}
                                {trigger.triggerType === 'status_change' && trigger.toStatus && (
                                  <span> → {(() => {
                                    const status = statusOptions.find(opt => opt.opt_id.toString() === trigger.toStatus);
                                    return status ? status.opt_name : trigger.toStatus;
                                  })()}</span>
                                )}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                Template: {(() => {
                                  console.log('Template lookup debug:', {
                                    triggerTemplateId: trigger.templateId,
                                    triggerTemplateIdType: typeof trigger.templateId,
                                    availableTemplates: templates.map(t => ({ id: t.id, idType: typeof t.id, name: t.name })),
                                    triggerData: trigger
                                  });
                                  const template = templates.find(t => t.id.toString() === trigger.templateId?.toString());
                                  return template ? template.name : `Unknown Template (ID: ${trigger.templateId || 'N/A'})`;
                                })()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleEditTrigger(trigger)}
                              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                              title="Edit Rule"
                            >
                              <Settings className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteTrigger(trigger)}
                              className="p-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                              title="Delete Rule"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <div>
            <div className="mb-6 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Message Logs
              </h3>
              <div className="flex items-center space-x-2">
                {['all', 'sent', 'failed'].map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setLogsFilter(filter as any)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      logsFilter === filter
                        ? 'bg-brandColor/10 text-brandColor dark:bg-brandColor/20 dark:text-brandColor'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Recipient
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Template
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {logs.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center">
                          <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-500 dark:text-gray-400">No messages sent yet</p>
                        </td>
                      </tr>
                    ) : (
                      logs.map((log, index) => (
                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                            {log.time}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                            {log.recipient}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                            {log.template}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              log.status === 'sent'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : log.status === 'failed'
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                            }`}>
                              {log.status === 'sent' && <CheckCircle className="h-3 w-3 mr-1" />}
                              {log.status === 'failed' && <XCircle className="h-3 w-3 mr-1" />}
                              {log.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                              {log.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {log.details}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirmation.show && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setDeleteConfirmation({show: false, trigger: null})}
            ></div>

            {/* Modal */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20 sm:mx-0 sm:h-10 sm:w-10">
                    <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                      Delete Automation Rule
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete "{deleteConfirmation.trigger?.name}"? This action cannot be undone and will stop all automated messages for this rule.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmDeleteTrigger}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteConfirmation({show: false, trigger: null})}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Automation Rule Right Slider */}
      {showTriggerSlider && (
        <AutomationRuleSlider
          isOpen={showTriggerSlider}
          onClose={() => {
            setShowTriggerSlider(false);
            setEditingTrigger(null);
          }}
          trigger={editingTrigger}
          templates={templates}
          crmColumns={crmColumns}
          statusOptions={statusOptions}
          onSave={async (triggerData) => {
            try {
              const result = await watiService.saveTrigger(triggerData);
              if (result.success) {
                toast.success('Automation rule saved successfully');
                setShowTriggerSlider(false);
                setEditingTrigger(null);
                await loadTriggers();
              } else {
                toast.error(result.message || 'Failed to save automation rule');
              }
            } catch (error) {
              toast.error('Failed to save automation rule');
              console.error('Error saving trigger:', error);
            }
          }}
        />
      )}
    </div>
  );
};

// Automation Rule Slider Component
interface AutomationRuleSliderProps {
  isOpen: boolean;
  onClose: () => void;
  trigger: TriggerRule | null;
  templates: Template[];
  crmColumns: any[];
  statusOptions: any[];
  onSave: (triggerData: any) => Promise<void>;
}

const AutomationRuleSlider: React.FC<AutomationRuleSliderProps> = ({
  isOpen,
  onClose,
  trigger,
  templates,
  crmColumns,
  statusOptions,
  onSave
}) => {
  const [formData, setFormData] = useState({
    trigger_name: '',
    trigger_event: 'new_lead' as 'new_lead' | 'status_change' | 'custom',
    template_id: '',
    trigger_conditions: {} as any,
    parameter_mapping: {} as Record<string, string>,
    media_url: '', // For templates with media attachments
    channel: 'whatsapp',
    is_active: true,
    priority: 1
  });

  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (trigger) {
      setFormData({
        trigger_name: trigger.name || trigger.trigger_name || '',
        trigger_event: trigger.triggerType || trigger.trigger_event || 'new_lead',
        template_id: (trigger.templateId || trigger.template_id) ? String(trigger.templateId || trigger.template_id) : '',
        trigger_conditions: {
          ...trigger.trigger_conditions,
          target_status: (() => {
            // If toStatus exists, handle both opt_id and opt_value formats
            if (trigger.toStatus) {
              // First check if it's already an opt_id
              const statusByOptId = statusOptions.find(opt => opt.opt_id.toString() === trigger.toStatus);
              if (statusByOptId) {
                return trigger.toStatus;
              }

              // If not found by opt_id, try to find by opt_value (for backward compatibility)
              const statusByOptValue = statusOptions.find(opt => opt.opt_value === trigger.toStatus);
              return statusByOptValue ? statusByOptValue.opt_id.toString() : trigger.toStatus;
            }
            return trigger.trigger_conditions?.target_status || '';
          })()
        },
        parameter_mapping: trigger.parameterMapping || trigger.parameter_mapping || {},
        media_url: trigger.parameterMapping?.media_url || trigger.parameter_mapping?.media_url || '',
        channel: trigger.channel || 'whatsapp',
        is_active: trigger.isActive !== undefined ? trigger.isActive : (trigger.is_active !== undefined ? trigger.is_active : true),
        priority: trigger.priority || 1
      });
    } else {
      setFormData({
        trigger_name: '',
        trigger_event: 'new_lead',
        template_id: '',
        trigger_conditions: {},
        parameter_mapping: {},
        media_url: '',
        channel: 'whatsapp',
        is_active: true,
        priority: 1
      });
    }
  }, [trigger, statusOptions]);

  useEffect(() => {
    const template = templates.find(t => t.id.toString() === formData.template_id);
    setSelectedTemplate(template || null);

    // Reset parameter mapping when template changes
    if (template) {
      const newMapping: Record<string, string> = {};
      template.parameters.forEach((_param, index) => {
        newMapping[`${index + 1}`] = formData.parameter_mapping[`${index + 1}`] || '';
      });
      setFormData(prev => ({ ...prev, parameter_mapping: newMapping }));
    }
  }, [formData.template_id, templates]);

  const handleSave = async () => {
    if (!formData.trigger_name || !formData.template_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    try {
      // Include media URL in parameter mapping if provided
      const parameterMapping = { ...formData.parameter_mapping };
      if (formData.media_url) {
        parameterMapping.media_url = formData.media_url;
      }

      const { media_url, ...formDataWithoutMedia } = formData;

      const triggerData = {
        ...formDataWithoutMedia,
        template_id: parseInt(formData.template_id),
        parameter_mapping: parameterMapping,
        ...(trigger && { id: trigger.id })
      };

      // Set trigger conditions based on event type
      if (formData.trigger_event === 'status_change') {
        if (!formData.trigger_conditions.target_status) {
          toast.error('Please select the target status for status change trigger');
          setIsSaving(false);
          return;
        }
      }

      await onSave(triggerData);
    } catch (error) {
      console.error('Error saving trigger:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SettingsSlideModal
      isOpen={isOpen}
      onClose={onClose}
      title={trigger ? 'Edit Automation Rule' : 'Create Automation Rule'}
      subtitle={trigger ? 'Modify automation settings and parameter mapping' : 'Set up automated WhatsApp messages for CRM events'}
      icon={<Zap className="w-6 h-6" />}
    >
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Rule Name *
              </label>
              <input
                type="text"
                value={formData.trigger_name}
                onChange={(e) => setFormData({ ...formData, trigger_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="e.g., Welcome New Leads"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Trigger Event *
              </label>
              <select
                value={formData.trigger_event}
                onChange={(e) => setFormData({
                  ...formData,
                  trigger_event: e.target.value as 'new_lead' | 'status_change',
                  trigger_conditions: {} // Reset conditions when changing event type
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="new_lead">New Lead</option>
                <option value="status_change">Status Change</option>
              </select>
            </div>
          </div>

          {/* Status Change Conditions */}
          {formData.trigger_event === 'status_change' && (
            <div className="p-4 bg-brandColor/10 dark:bg-brandColor/20 rounded-lg">
              <h4 className="text-sm font-medium text-brandColor dark:text-brandColor mb-3">
                Status Change Conditions
              </h4>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Target Status (trigger when lead changes to this status from any other status)
                  </label>
                  <select
                    value={formData.trigger_conditions.target_status || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      trigger_conditions: { ...formData.trigger_conditions, target_status: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select target status</option>
                    {statusOptions.map((option) => (
                      <option key={option.opt_id} value={option.opt_id}>
                        {option.opt_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Template Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              WhatsApp Template *
            </label>
            <select
              value={formData.template_id}
              onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Select a template</option>
              {templates.filter(t => t.status === 'APPROVED').map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name} ({template.category})
                </option>
              ))}
            </select>
          </div>

          {/* Template Preview */}
          {selectedTemplate && (
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Template Preview</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                {selectedTemplate.content}
              </p>
            </div>
          )}

          {/* Parameter Mapping */}
          {selectedTemplate && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                Parameter Mapping
              </h4>
              {selectedTemplate.parameters && selectedTemplate.parameters.length > 0 ? (
                <div className="space-y-3">
                  {selectedTemplate.parameters.map((_param, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-20">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {`{{${index + 1}}}`}
                        </span>
                      </div>
                      <div className="flex-1">
                        <select
                          value={formData.parameter_mapping[`${index + 1}`] || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            parameter_mapping: {
                              ...formData.parameter_mapping,
                              [`${index + 1}`]: e.target.value
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                        >
                          <option value="">Select field to map</option>
                          {crmColumns.map((column) => (
                            <option key={column.col_id} value={column.col_name}>
                              {column.col_label} ({column.col_name})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    This template doesn't have any parameters to map.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Media URL - Show only for templates with media attachments */}
          {selectedTemplate && (selectedTemplate.headerType === 'IMAGE' || selectedTemplate.headerType === 'VIDEO' || selectedTemplate.headerType === 'DOCUMENT') && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                Media Attachment
              </h4>
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-3">
                      This template requires a {selectedTemplate.headerType.toLowerCase()} attachment. Provide a URL to the media file.
                    </p>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Media URL ({selectedTemplate.headerType.toLowerCase()})
                      </label>
                      <input
                        type="url"
                        value={formData.media_url}
                        onChange={(e) => setFormData({
                          ...formData,
                          media_url: e.target.value
                        })}
                        placeholder={`https://example.com/media.${selectedTemplate.headerType === 'IMAGE' ? 'jpg' : selectedTemplate.headerType === 'VIDEO' ? 'mp4' : 'pdf'}`}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        The media URL will be used for all messages sent with this template
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Additional Settings */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Priority
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 1 })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Channel
              </label>
              <select
                value={formData.channel}
                onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                disabled
              >
                <option value="whatsapp">WhatsApp</option>
              </select>
            </div>

            <div className="flex items-center justify-center">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-brandColor rounded focus:ring-brandColor"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Active
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Footer */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-900">
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            {isSaving ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{isSaving ? 'Saving...' : (trigger ? 'Update Rule' : 'Create Rule')}</span>
          </button>
        </div>
      </div>
    </SettingsSlideModal>
  );
};

export default WatiSettings;